/**
 * InvoiceRecipientsCard — "Send this invoice to"
 *
 * SPEC-v017-completion-2026-09-27 R2. Christa, 2026-09-21, invoice 0729:
 *
 *   "where in the invoice section am I able to quickly reference the email of
 *    the person invoices should be sent to? I'm finding that I need to go back
 *    through my emails to see who to send these too... all the ones today I've
 *    had to go back and try and search in my emails to find who they were sent
 *    to."
 *
 * She does NOT send from Keystone — she downloads the PDF and sends from
 * Outlook. So this is a LOOKUP, not a send control: it sits high on the invoice
 * page, renders every address as plain selectable text, and gives her one click
 * to put the whole list on the clipboard in Outlook's separator. Nothing here
 * sends anything, and nothing here belongs in the Send modal.
 *
 * Why it is separate from BillToPanel's Recipients row: that row is an EDITOR
 * (pencils, star toggles, remove-with-propagation) over `invoice_contacts`
 * ONLY. For 216 of her 500 organizations there is no contact at all, so that
 * row is empty on the common path and its empty copy is written about SENDING
 * ("Email will silently fall back...") — a sentence that is true of a flow she
 * never uses. This card answers a different question, off `recipient_lookup`,
 * which resolves past the invoice into the project pin, the client's accounting
 * contacts, the last invoice issued to that client, and the client's main
 * contact — and LABELS which one it used.
 *
 * The honesty rules, both load-bearing:
 *   1. An inferred address is never shown as if it were recorded on the
 *      invoice. She is about to paste it into Outlook and send money-related
 *      mail to it. `recorded_on_invoice` drives the prefix; the backend never
 *      writes that prefix into `origin_label`, so the two cannot disagree.
 *   2. Nothing here ever says the invoice HAS no recipient. When Keystone knows
 *      nobody, the missing thing is the record, not the person.
 *
 * QA round 3 added a third, of the same family: the card must never show a
 * SHORTER list than the document. An invoice that records only a CC used to
 * render the CC alone with an empty TO line, while the attached PDF was
 * addressed to the org Primary through `_resolve_to_contacts`' fallback — so
 * the block she pastes into Outlook dropped the actual addressee. `to_unresolved`
 * carries that person, and the card names them WITHOUT filing them under
 * "recorded on this invoice", because they are not.
 */
import { useState } from 'react';
import { Copy, Check, Mail, Plus, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';

export interface RecipientLookupRow {
  name: string | null;
  email: string | null;
  role: string;
  missing_email: boolean;
}

/**
 * Who the ATTACHED PDF will put in its Attention block when the invoice
 * records no TO of its own. NOT a recipient: it is not on the invoice, so it
 * is never merged into `recipients`, `copy_text*` or `emailable_count`.
 */
export interface UnresolvedToContact {
  name: string | null;
  email: string | null;
  origin: string;
  origin_label: string;
}

export interface RecipientLookup {
  recipients: RecipientLookupRow[];
  origin: string;
  origin_label: string;
  recorded_on_invoice: boolean;
  /** Every address. Only offered as ONE action when there is no CC. */
  copy_text: string;
  /** The To line alone. Older backends do not send it — fall back to copy_text. */
  copy_text_to?: string;
  /** The Cc line alone. */
  copy_text_cc?: string;
  emailable_count: number;
  empty_reason: string | null;
  /**
   * QA round 3. Set when this invoice records no TO line but the PDF is still
   * addressed to somebody — a CC-only invoice, or an empty answer against a
   * client with a main contact. Null (or absent, on an older backend) when the
   * page and the document already agree.
   */
  to_unresolved?: UnresolvedToContact | null;
}

interface InvoiceRecipientsCardProps {
  lookup: RecipientLookup | null | undefined;
  /** Her name for the client, for the empty-state sentence. */
  clientName?: string | null;
  /**
   * False when recipients cannot be recorded on this invoice at all. The card
   * then says where to put the address instead of offering a link that does
   * nothing.
   */
  canAddRecipient?: boolean;
  /**
   * Takes her to the place recipients are actually recorded (BILL TO).
   * Returns FALSE when that place is not on the page, so the card can say so
   * rather than swallow the click — see MED-1 in R2_OUTPUT.md.
   */
  onAddRecipient?: () => boolean | void;
}

/**
 * Clipboard with a real fallback. happy-dom, an http origin and a denied
 * permission all leave `navigator.clipboard` unusable, and a copy button that
 * silently does nothing is worse than no copy button — she would paste the
 * PREVIOUS clipboard contents into an invoice email.
 */
async function copyToClipboard(text: string): Promise<boolean> {
  try {
    if (navigator?.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    /* fall through to the textarea route */
  }
  try {
    const el = document.createElement('textarea');
    el.value = text;
    el.setAttribute('readonly', '');
    el.style.position = 'fixed';
    el.style.opacity = '0';
    document.body.appendChild(el);
    el.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(el);
    return ok;
  } catch {
    return false;
  }
}

function CopyButton({
  text,
  label,
  testId,
}: {
  text: string;
  label: string;
  testId: string;
}) {
  const [copied, setCopied] = useState(false);
  return (
    <Button
      type="button"
      variant="outline"
      size="sm"
      className="h-7 px-2 text-xs"
      data-testid={testId}
      onClick={async () => {
        const ok = await copyToClipboard(text);
        if (ok) {
          setCopied(true);
          window.setTimeout(() => setCopied(false), 1500);
        }
      }}
    >
      {copied ? (
        <>
          <Check className="w-3.5 h-3.5 mr-1" aria-hidden="true" />
          Copied
        </>
      ) : (
        <>
          <Copy className="w-3.5 h-3.5 mr-1" aria-hidden="true" />
          {label}
        </>
      )}
    </Button>
  );
}

/**
 * The empty sentence. Three distinct causes, three distinct fixes — and none of
 * them is "this invoice has no recipient".
 */
function emptyCopy(lookup: RecipientLookup, client: string): string {
  const named = lookup.recipients
    .map((r) => r.name)
    .filter(Boolean)
    .join(', ');

  if (lookup.empty_reason === 'attention_line_suppressed') {
    // V175's per-invoice opt-out, which is also what the FE sets when she
    // takes the last recipient off an invoice. The PDF prints no Attention
    // block, so the page must say the same thing the document says.
    return (
      'Recipients were removed from this invoice, so its PDF prints no ' +
      'Attention line and Keystone is not suggesting anyone. Add an address ' +
      'here if that was not intended.'
    );
  }
  if (lookup.empty_reason === 'project_recipients_cleared') {
    return (
      'Recipients were deliberately cleared for this project, so Keystone is ' +
      'not suggesting anyone. Add an address here if that was not intended.'
    );
  }
  if (lookup.empty_reason === 'contact_has_no_email') {
    return named
      ? `Keystone has ${named} for ${client}, but no email address on file. ` +
          'Add the address and it will be here next time.'
      : `Keystone has a contact for ${client}, but no email address on file.`;
  }
  return (
    `Keystone has no email address to suggest for this invoice to ${client}. ` +
    'Add a recipient here, then choose whether to save it for future invoices.'
  );
}

/** One row: the name, the address as selectable text, and its own Copy. */
function RecipientRow({
  row,
  testId,
}: {
  row: RecipientLookupRow;
  testId: string;
}) {
  return (
    <li className="flex items-center gap-3 flex-wrap" data-testid={testId}>
      {row.name && (
        <span className="text-sm font-medium text-slate-900">{row.name}</span>
      )}
      {row.email ? (
        <>
          {/* select-text + break-all: she highlights and copies these by hand
              as often as she uses the button, and a truncated address is a
              wrong address. */}
          <span className="text-sm text-slate-700 select-text break-all">
            {row.email}
          </span>
          <CopyButton text={row.email} label="Copy" testId={`${testId}-copy`} />
        </>
      ) : (
        <span className="text-sm text-slate-500 italic">
          no email address on file
        </span>
      )}
    </li>
  );
}

/**
 * A role group with its heading. The vocabulary is the invoice LIST's —
 * `RecipientCell` has rendered TO / CC chips since v0.12, and the two surfaces
 * describing the same rows differently is its own kind of wrong.
 */
function RoleGroup({
  role,
  rows,
  copyText,
  copyLabel,
  copyTestId,
}: {
  role: 'to' | 'cc';
  rows: RecipientLookupRow[];
  copyText: string;
  copyLabel: string;
  copyTestId: string;
}) {
  if (rows.length === 0) return null;
  return (
    <div className="mt-1" data-testid={`invoice-recipient-group-${role}`}>
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-xs font-semibold text-muted-foreground tracking-wider">
          {role.toUpperCase()}
        </span>
        {copyText && (
          <CopyButton text={copyText} label={copyLabel} testId={copyTestId} />
        )}
      </div>
      <ul className="space-y-1.5 mt-1">
        {rows.map((row, idx) => (
          <RecipientRow
            key={`${row.email ?? 'no-email'}-${row.name ?? 'no-name'}-${idx}`}
            row={row}
            testId={`invoice-recipient-${role}-row-${idx}`}
          />
        ))}
      </ul>
    </div>
  );
}

export function InvoiceRecipientsCard({
  lookup,
  clientName,
  canAddRecipient = true,
  onAddRecipient,
}: InvoiceRecipientsCardProps) {
  // Set when the Add button was clicked and there was nothing to open. A click
  // that does nothing, with nothing on screen to explain it, is worse than no
  // button — she is usually chasing payment when she needs this.
  const [addUnavailable, setAddUnavailable] = useState(false);

  // An older backend (or a failed projection) sends nothing. Render nothing
  // rather than an empty box that reads like an answer.
  if (!lookup) return null;

  const client = (clientName || '').trim() || 'this client';
  const hasAddresses = lookup.emailable_count > 0;
  const hasRows = lookup.recipients.length > 0;

  // 2026-09-21: "there is one person it goes to with another cc'd." TO and CC
  // are NEVER merged into one paste — a semicolon-joined blob goes into
  // Outlook's To field, and a person she meant to CC becomes a visible
  // addressee on a client's invoice email.
  const ccRows = lookup.recipients.filter((r) => r.role === 'cc');
  const toRows = lookup.recipients.filter((r) => r.role !== 'cc');
  const ccAddresses = ccRows.filter((r) => r.email);
  const toAddresses = toRows.filter((r) => r.email);
  // Older backend: no split fields. Fall back to the joined list for the TO
  // line only when there is provably no CC to merge into it.
  const copyTo =
    lookup.copy_text_to ??
    (ccAddresses.length === 0 ? lookup.copy_text : toAddresses.map((r) => r.email).join('; '));
  const copyCc =
    lookup.copy_text_cc ?? ccAddresses.map((r) => r.email).join('; ');
  // The combined action survives ONLY where it is unambiguous: everyone is a
  // TO, so "all addresses" and "the To line" are the same list.
  const rolesMixed = ccAddresses.length > 0;
  const showCopyAll = !rolesMixed && lookup.emailable_count > 1;
  // QA round 3: who the PDF will address when this invoice records no TO.
  const toUnresolved = lookup.to_unresolved ?? null;
  // The Add control used to be gated on `!hasAddresses` alone, so ONE recorded
  // CC suppressed the only control that would let her record the real
  // recipient — on exactly the invoice where the document and the screen
  // disagree. A missing TO is a thing to fix whether or not a CC is present.
  const needsAnAddress = !hasAddresses || toUnresolved !== null;
  const showAddLink = needsAnAddress && canAddRecipient && !addUnavailable;
  const showAddUnavailable = needsAnAddress && (!canAddRecipient || addUnavailable);

  return (
    <div
      className="mb-6 rounded-lg border border-slate-200 bg-slate-50 p-4"
      data-testid="invoice-recipient-lookup"
    >
      <div className="flex items-start justify-between gap-3 mb-2">
        <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider flex items-center gap-1.5">
          <Mail className="w-3.5 h-3.5" aria-hidden="true" />
          Send this invoice to
        </p>
        {showCopyAll && (
          <CopyButton
            text={lookup.copy_text}
            label="Copy all addresses"
            testId="invoice-recipient-copy-all"
          />
        )}
      </div>

      {hasRows && (
        <div data-testid="invoice-recipient-list">
          <RoleGroup
            role="to"
            rows={toRows}
            copyText={rolesMixed ? copyTo : ''}
            copyLabel="Copy TO"
            copyTestId="invoice-recipient-copy-to"
          />
          <RoleGroup
            role="cc"
            rows={ccRows}
            copyText={rolesMixed ? copyCc : ''}
            copyLabel="Copy CC"
            copyTestId="invoice-recipient-copy-cc"
          />
        </div>
      )}

      {hasAddresses && (
        <p
          className="mt-2 text-sm text-slate-600"
          data-testid="invoice-recipient-origin"
        >
          {lookup.recorded_on_invoice ? (
            // A CC-only set IS recorded — but "these are recorded on this
            // invoice" over it reads as if the whole question is answered,
            // while the TO line is empty and the PDF is addressed to someone
            // else entirely. Name what is actually recorded.
            toUnresolved && toRows.length === 0 ? (
              <>These Cc addresses are recorded on this invoice.</>
            ) : (
              <>These are recorded on this invoice.</>
            )
          ) : (
            <>
              <span className="font-medium text-amber-800">
                Not recorded on this invoice
              </span>
              {lookup.origin_label ? ` — ${lookup.origin_label}.` : '.'}{' '}
              Worth a glance before you send it.
            </>
          )}
        </p>
      )}

      {!hasAddresses && (
        <div
          className="mt-1 rounded-md bg-amber-50 border border-amber-200 px-3 py-2 text-sm text-amber-900"
          data-testid="invoice-recipient-empty"
        >
          <div className="flex items-start gap-2">
            <AlertTriangle
              className="w-4 h-4 mt-0.5 flex-shrink-0 text-amber-700"
              aria-hidden="true"
            />
            <p>{emptyCopy(lookup, client)}</p>
          </div>
        </div>
      )}

      {toUnresolved && (
        <div
          className="mt-2 rounded-md bg-amber-50 border border-amber-200 px-3 py-2 text-sm text-amber-900"
          data-testid="invoice-recipient-to-unresolved"
        >
          <div className="flex items-start gap-2">
            <AlertTriangle
              className="w-4 h-4 mt-0.5 flex-shrink-0 text-amber-700"
              aria-hidden="true"
            />
            <div>
              <p>
                This invoice records no TO recipient. The PDF will be addressed
                to{' '}
                <span className="font-medium">
                  {toUnresolved.name || `${client}'s main contact`}
                </span>
                {toUnresolved.origin_label ? ` — ${toUnresolved.origin_label}.` : '.'}
              </p>
              {toUnresolved.email && (
                /* select-text: she pastes this into Outlook by hand as often
                   as she uses a button, and a name alone addresses nothing. */
                <p className="mt-0.5 select-text break-all">{toUnresolved.email}</p>
              )}
            </div>
          </div>
        </div>
      )}

      {showAddLink && (
        <div className="mt-2">
          <button
            type="button"
            className="text-sm text-blue-700 hover:underline inline-flex items-center gap-1"
            data-testid="invoice-recipient-add"
            onClick={() => {
              // `false` means BILL TO's Add-recipient trigger is not on this
              // page. Say where the address goes instead of doing nothing.
              if (onAddRecipient?.() === false) setAddUnavailable(true);
            }}
          >
            <Plus className="w-3.5 h-3.5" aria-hidden="true" />
            {/* With a CC already recorded, "add an email address" describes
                the wrong gap — the missing thing is the TO line. */}
            {toUnresolved ? 'Add a TO recipient' : 'Add an email address'}
          </button>
        </div>
      )}

      {showAddUnavailable && (
        <p
          className="mt-2 text-sm text-slate-600"
          data-testid="invoice-recipient-add-unavailable"
        >
          {`${
            canAddRecipient
              ? 'There is nowhere on this invoice to record it.'
              : 'Recipients cannot be recorded on this invoice.'
          } You can save the address in ${client}'s accounting contacts for future invoices. Project and invoice recipient choices still take precedence.`}
        </p>
      )}
    </div>
  );
}
